import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { Connector, SyncResult } from '@/types/connector';
import { getAccessToken } from '@/utils/jwt';

/** Shape returned by the backend connector registry API. */
interface BackendConnector {
  connector_id: string;
  connector_type: string;
  enabled: boolean;
  capabilities: string[];
  config: Record<string, unknown>;
}

const CONNECTOR_LABELS: Record<string, string> = {
  shotgrid: 'ShotGrid',
  slack: 'Slack',
  google_drive: 'Google Drive',
  sharepoint: 'SharePoint',
  confluence: 'Confluence',
  web: 'Web Scraper',
  microagent: 'Microagent',
};

function mapConnector(c: BackendConnector): Connector {
  return {
    id: c.connector_id,
    name: CONNECTOR_LABELS[c.connector_type] ?? c.connector_type,
    type: c.connector_type,
    description: `${CONNECTOR_LABELS[c.connector_type] ?? c.connector_type} connector (${c.connector_id})`,
    health: {
      status: c.enabled ? 'HEALTHY' : 'DISCONNECTED',
      last_check: new Date().toISOString(),
      message: c.enabled ? 'Active' : 'Disabled',
    },
    capabilities: c.capabilities,
    config: c.config,
    enabled: c.enabled,
    created_at: new Date().toISOString(),
  };
}

export const connectorsApi = createApi({
  reducerPath: 'connectorsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Connector'],
  endpoints: (builder) => ({
    listConnectors: builder.query<Connector[], undefined>({
      query: () => '/connectors',
      transformResponse: (response: { success: boolean; connectors: BackendConnector[] }) =>
        response.connectors.map(mapConnector),
      providesTags: ['Connector'],
    }),
    getConnector: builder.query<Connector, string>({
      query: (id) => `/connectors/${id}`,
      transformResponse: (response: { connector: BackendConnector }) =>
        mapConnector(response.connector),
      providesTags: (_r, _e, id) => [{ type: 'Connector', id }],
    }),
    syncConnector: builder.mutation<SyncResult, string>({
      query: (id) => ({ url: `/connectors/${id}/sync`, method: 'POST' }),
      transformResponse: (response: { sync_result: SyncResult }) => response.sync_result,
      invalidatesTags: ['Connector'],
    }),
    updateConnectorConfig: builder.mutation<
      Connector,
      { id: string; config: Record<string, unknown> }
    >({
      query: ({ id, config }) => ({
        url: `/connectors/${id}/config`,
        method: 'PUT',
        body: config,
      }),
      transformResponse: (response: { connector: BackendConnector }) =>
        mapConnector(response.connector),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Connector', id }],
    }),
    enableConnector: builder.mutation<Connector, string>({
      query: (id) => ({ url: `/connectors/${id}/enable`, method: 'POST' }),
      transformResponse: (response: { connector: BackendConnector }) =>
        mapConnector(response.connector),
      invalidatesTags: (_r, _e, id) => [{ type: 'Connector', id }],
    }),
    disableConnector: builder.mutation<Connector, string>({
      query: (id) => ({ url: `/connectors/${id}/disable`, method: 'POST' }),
      transformResponse: (response: { connector: BackendConnector }) =>
        mapConnector(response.connector),
      invalidatesTags: (_r, _e, id) => [{ type: 'Connector', id }],
    }),
  }),
});

export const {
  useListConnectorsQuery,
  useGetConnectorQuery,
  useSyncConnectorMutation,
  useUpdateConnectorConfigMutation,
  useEnableConnectorMutation,
  useDisableConnectorMutation,
} = connectorsApi;
